import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, Users, MoreVertical, Copy, Edit, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingState } from '../../components/shared/LoadingState';
import { ErrorState } from '../../components/shared/ErrorState';
import { EmptyState } from '../../components/shared/EmptyState';
import { DataTable } from '../../components/shared/DataTable';
import { DocumentPreviewDialog } from './components/DocumentPreviewDialog';
import { SignatureRequestDialog } from './components/SignatureRequestDialog';
import { SignatureCanvas } from './components/SignatureCanvas';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/DropdownMenu';
import { useDocuments } from '../../core/hooks/useDocuments';
import { useClients } from '../../core/hooks/useClients';
import { useAuth } from '../../core/contexts/AuthContext';
import { DocumentStatus, DocumentSignatureStatus } from '../../core/models/Document';
import { UserRole } from '../../core/models/User';
import { ColumnDef } from '@tanstack/react-table';
import type { Document, DocumentSignature } from '../../core/models/Document';

export const DocumentsPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, organizationId } = useAuth();
  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN;
  // For super admins, pass null to fetch all clients; otherwise use organizationId
  const effectiveOrgIdForClients = isSuperAdmin ? null : organizationId;
  const { documents, loading, error, updateDocument, duplicateDocument, deleteDocument } = useDocuments(null, organizationId);
  const { clients, loading: clientsLoading } = useClients(effectiveOrgIdForClients);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [signatureRequestOpen, setSignatureRequestOpen] = useState(false);
  const [signatureCanvasOpen, setSignatureCanvasOpen] = useState(false);
  const [duplicatingDocumentId, setDuplicatingDocumentId] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);
  const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(null);

  // Create a map of client IDs to client names for quick lookup
  const clientMap = useMemo(() => {
    const map = new Map<string, string>();
    clients.forEach((client) => {
      map.set(client.id, client.name);
    });
    return map;
  }, [clients]);

  const getStatusColor = (status: DocumentStatus) => {
    switch (status) {
      case DocumentStatus.COMPLETED:
        return 'success';
      case DocumentStatus.PENDING_SIGNATURES:
        return 'warning';
      case DocumentStatus.CANCELLED:
        return 'error';
      case DocumentStatus.EXPIRED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getClientName = (clientId: string) => {
    return clientMap.get(clientId) || 'Unknown Project';
  };

  const handleViewDocument = (document: Document) => {
    setSelectedDocument(document);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setSelectedDocument(null);
  };

  const handleRequestSignatures = async (signatures: DocumentSignature[]) => {
    if (!selectedDocument) return;

    try {
      await updateDocument(selectedDocument.id, {
        signatures,
        status: DocumentStatus.PENDING_SIGNATURES,
      });

      setSelectedDocument({
        ...selectedDocument,
        signatures,
        status: DocumentStatus.PENDING_SIGNATURES,
      });

      setSignatureRequestOpen(false);
    } catch {
      alert('Failed to request signatures. Please try again.');
    }
  };

  const handleSign = async (signatureData?: string) => {
    // Signature image data is currently not persisted; reserved for future use
    void signatureData;
    if (!selectedDocument || !currentUser) return;

    try {
      const updatedSignatures = selectedDocument.signatures.map((sig) => {
        if (sig.userId === currentUser.uid && sig.status === DocumentSignatureStatus.PENDING) {
          return {
            ...sig,
            status: DocumentSignatureStatus.SIGNED,
            signedAt: new Date(),
          };
        }
        return sig;
      });

      const allSigned = updatedSignatures.every(
        (sig) => sig.status === DocumentSignatureStatus.SIGNED
      );

      await updateDocument(selectedDocument.id, {
        signatures: updatedSignatures,
        status: allSigned ? DocumentStatus.COMPLETED : DocumentStatus.PENDING_SIGNATURES,
      });

      setSelectedDocument({
        ...selectedDocument,
        signatures: updatedSignatures,
        status: allSigned ? DocumentStatus.COMPLETED : DocumentStatus.PENDING_SIGNATURES,
      });

      setSignatureCanvasOpen(false);
    } catch {
      alert('Failed to sign document. Please try again.');
    }
  };

  const handleDuplicateDocument = async (document: Document) => {
    try {
      setDuplicatingDocumentId(document.id);
      await duplicateDocument(document.id);
      // The documents list will automatically update via the subscription
    } catch (err: any) {
      console.error('Failed to duplicate document:', err);
      alert(err?.message || 'Failed to duplicate document. Please try again.');
    } finally {
      setDuplicatingDocumentId(null);
    }
  };

  const handleDeleteClick = (document: Document) => {
    setDocumentToDelete(document);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;

    try {
      setDeletingDocumentId(documentToDelete.id);
      await deleteDocument(documentToDelete.id, documentToDelete.clientId);
      setDeleteConfirmOpen(false);
      setDocumentToDelete(null);
    } catch (err: any) {
      console.error('Failed to delete document:', err);
      alert(err?.message || 'Failed to delete document. Please try again.');
    } finally {
      setDeletingDocumentId(null);
    }
  };

  const columns: ColumnDef<Document>[] = [
    {
      accessorKey: 'title',
      header: 'Title',
      cell: ({ row }) => (
        <div className="font-medium">{row.original.title}</div>
      ),
    },
    {
      accessorKey: 'clientId',
      header: 'Project',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm">{getClientName(row.original.clientId)}</span>
          <Badge variant="outline" className="text-xs">
            Project Document
          </Badge>
        </div>
      ),
    },
    {
      accessorKey: 'templateType',
      header: 'Template Type',
      cell: ({ row }) => (
        <Badge variant="default" className="text-xs">
          {row.original.templateType?.name || 'Unknown'}
        </Badge>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={getStatusColor(row.original.status)}>
          {row.original.status}
        </Badge>
      ),
    },
    {
      accessorKey: 'version',
      header: 'Version',
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) =>
        new Date(row.original.createdAt).toLocaleDateString(),
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const document = row.original;
        const isDuplicating = duplicatingDocumentId === document.id;
        const isDeleting = deletingDocumentId === document.id;
        const isDisabled = isDuplicating || isDeleting;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" disabled={isDisabled}>
                {isDisabled ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                ) : (
                  <MoreVertical className="h-4 w-4" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleViewDocument(document)}>
                <FileText className="mr-2 h-4 w-4" />
                View
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate(`/documents/${document.id}/edit`)}
              >
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleDuplicateDocument(document)}
                disabled={isDuplicating}
              >
                <Copy className="mr-2 h-4 w-4" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleDeleteClick(document)}
                className="text-red-600 focus:text-red-600"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  if (loading || clientsLoading) {
    return <LoadingState message="Loading documents..." />;
  }

  if (error) {
    return <ErrorState message="Failed to load documents" />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Project Documents"
        description="View and manage project-specific documents created from templates or markdown shells"
        icon={FileText}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/documents/new/markdown')}>
              <Plus className="mr-2 h-4 w-4" />
              Markdown Shell
            </Button>
            <Button onClick={() => navigate('/documents/new')}>
              <Plus className="mr-2 h-4 w-4" />
              From Template
            </Button>
          </div>
        }
      />

      {documents.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No documents found"
          description="Create your first document from a template or markdown shell to get started"
          action={{
            label: 'Create Document from Template',
            onClick: () => navigate('/documents/new'),
          }}
        />
      ) : (
        <DataTable columns={columns} data={documents} searchKey="title" searchPlaceholder="Search documents..." />
      )}

      {/* Document Preview Modal */}
      {selectedDocument && (
        <DocumentPreviewDialog
          open={previewOpen}
          onClose={handleClosePreview}
          document={selectedDocument}
          onRequestSignatures={() => setSignatureRequestOpen(true)}
          onSign={() => setSignatureCanvasOpen(true)}
        />
      )}

      {/* Signature Request Dialog */}
      {selectedDocument && (
        <SignatureRequestDialog
          open={signatureRequestOpen}
          onClose={() => setSignatureRequestOpen(false)}
          onSubmit={handleRequestSignatures}
          documentTitle={selectedDocument.title}
        />
      )}

      {/* Signature Canvas Dialog */}
      {selectedDocument && (
        <Dialog open={signatureCanvasOpen} onOpenChange={setSignatureCanvasOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Sign Document</DialogTitle>
            </DialogHeader>
            <SignatureCanvas
              onSave={handleSign}
              onCancel={() => setSignatureCanvasOpen(false)}
              userName={currentUser?.displayName || currentUser?.email || undefined}
              userTitle={selectedDocument.signatures.find(
                (sig) => sig.userId === currentUser?.uid && sig.status === DocumentSignatureStatus.PENDING
              )?.title}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{documentToDelete?.title}"? This will permanently remove the document and all associated data including:
            </DialogDescription>
          </DialogHeader>
          <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1 my-4">
            <li>Document content and metadata</li>
            <li>All signature requests and records</li>
            <li>Generated PDFs and signed documents</li>
          </ul>
          <p className="text-sm text-red-600 font-medium">This action cannot be undone.</p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setDocumentToDelete(null);
              }}
              disabled={!!deletingDocumentId}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              loading={!!deletingDocumentId}
              disabled={!!deletingDocumentId}
            >
              Delete Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};
